import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { BRAND } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PlayCircle, LogOut, VideoOff, Video, ListChecks, Printer, Plus, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Eye, Share2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REASONS = ["Internet", "Power", "Camera", "DVR", "Other"];
const PIN_KEY = "sw_operator_pin";

export default function Monitoring() {
  const [pin, setPin] = useState(sessionStorage.getItem(PIN_KEY) || "");
  const [authOk, setAuthOk] = useState(!!sessionStorage.getItem(PIN_KEY));

  if (!authOk) return <PinGate onSuccess={(p) => { sessionStorage.setItem(PIN_KEY, p); setPin(p); setAuthOk(true); }} />;
  return <Dashboard pin={pin} onLogout={() => { sessionStorage.removeItem(PIN_KEY); setAuthOk(false); }} />;
}

/* ------------------------------------------------------------------ */
function PinGate({ onSuccess }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/monitoring/auth`, { pin });
      onSuccess(pin);
    } catch { toast.error("Invalid PIN"); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen grid place-items-center bg-black text-white px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0F0F13] p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-lg border border-brand-cyan/40 overflow-hidden bg-white/5">
            <img src={BRAND.logoUrl} alt="Secure Watch" className="h-full w-full object-contain" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-white/50">Secure Watch</div>
            <div className="font-heading text-xl font-extrabold">Night Monitoring</div>
          </div>
        </div>
        <div>
          <Label className="text-white/70 text-xs">Operator PIN</Label>
          <Input data-testid="pin-input" type="password" autoFocus inputMode="numeric" maxLength={12} value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••" className="bg-black/40 border-white/10 text-lg tracking-widest" />
        </div>
        <Button data-testid="pin-submit" type="submit" disabled={busy || !pin} className="w-full bg-brand-cyan text-black font-bold hover:bg-brand-cyan/90">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Unlock
        </Button>
        <div className="text-[11px] text-white/40 text-center">Monitoring window · 10:00 PM – 6:00 AM</div>
      </form>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function Dashboard({ pin, onLogout }) {
  const hdrs = { headers: { "X-Operator-Pin": pin } };
  const [operators, setOperators] = useState([]);
  const [clients, setClients] = useState([]);
  const [current, setCurrent] = useState(null);
  const [events, setEvents] = useState([]);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [op, cl, cur, li] = await Promise.all([
        axios.get(`${API}/monitoring/operators`, hdrs),
        axios.get(`${API}/clients`, { headers: { "X-Admin-Token": "securewatch2026" } }),
        axios.get(`${API}/monitoring/shifts/current`, hdrs),
        axios.get(`${API}/monitoring/live`, hdrs),
      ]);
      setOperators(op.data || []);
      setClients((cl.data || []).filter((c) => c.is_monitored !== false));
      setCurrent(cur.data || null);
      setLive(li.data || null);
      if (cur.data?.id) {
        const ev = await axios.get(`${API}/monitoring/events?shift_id=${cur.data.id}`, hdrs);
        setEvents(ev.data || []);
      } else {
        setEvents([]);
      }
    } catch { toast.error("Failed to load monitoring data"); }
    setLoading(false);
  };

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);

  const startShift = async (payload) => {
    try {
      await axios.post(`${API}/monitoring/shifts`, payload, hdrs);
      toast.success("Shift started");
      load();
    } catch { toast.error("Could not start shift"); }
  };
  const logOffline = async (payload) => {
    try {
      await axios.post(`${API}/monitoring/events`, payload, hdrs);
      toast.success("Offline event logged");
      load();
    } catch { toast.error("Could not save event"); }
  };
  const markOnline = async (eid) => {
    try {
      await axios.patch(`${API}/monitoring/events/${eid}`, {}, hdrs);
      toast.success("Camera back online");
      load();
    } catch { toast.error("Could not mark online"); }
  };
  const completeShift = async (payload) => {
    if (!current?.id) return;
    try {
      await axios.post(`${API}/monitoring/shifts/${current.id}/complete`, payload, hdrs);
      toast.success("Shift completed — see History below");
      await load();
      // Scroll to History section
      setTimeout(() => {
        const h = document.querySelector('[data-testid="section-history"]');
        if (h) h.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    } catch { toast.error("Could not complete shift"); }
  };
  const addOperator = async (name) => {
    try {
      await axios.post(`${API}/monitoring/operators`, { name }, hdrs);
      const l = await axios.get(`${API}/monitoring/operators`, hdrs);
      setOperators(l.data || []);
    } catch { toast.error("Could not add operator"); }
  };
  const openReport = async (sid, mode = "print") => {
    try {
      const { data } = await axios.get(`${API}/monitoring/shifts/${sid}/report`, hdrs);
      const html = renderReportHtml(data, /*forCanvas*/ mode === "view");
      const w = window.open("", "_blank", "width=1000,height=1200");
      if (!w) return toast.error("Popup blocked");
      w.document.write(html); w.document.close();
    } catch { toast.error("Could not load report"); }
  };

  const shareReport = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/monitoring/shifts/${sid}/report`, hdrs);
      // Render into an off-screen container for html2canvas
      const holder = document.createElement("div");
      holder.style.cssText = "position:fixed;left:-99999px;top:0;width:900px;background:#fff;color:#111";
      holder.innerHTML = renderReportBody(data);
      document.body.appendChild(holder);
      const canvas = await html2canvas(holder, { backgroundColor: "#ffffff", scale: 2 });
      document.body.removeChild(holder);
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
      if (!blob) throw new Error("No blob");
      const filename = `secure-watch-shift-${data.shift?.date || "report"}.png`;
      const file = new File([blob], filename, { type: "image/png" });
      const s = data.shift || {}; const t = data.totals || {};
      const text = `SECURE WATCH — Night Shift ${s.date}\nOperator: ${s.operator}\nCameras: ${t.cameras_total} · Events: ${t.events_total} (Resolved ${t.events_resolved}, Pending ${t.events_pending})\nDowntime: ${Math.round(t.total_downtime_minutes || 0)} min`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Night Monitoring Report", text });
        toast.success("Report shared");
      } else {
        // Fallback: download image + open WhatsApp with pre-filled text
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
        toast.success("Image downloaded — attach on WhatsApp");
      }
    } catch { toast.error("Could not generate share image"); }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 bg-[#0F0F13] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg border border-brand-cyan/40 overflow-hidden bg-white/5">
              <img src={BRAND.logoUrl} alt="Secure Watch" className="h-full w-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-extrabold leading-tight">SECURE WATCH · <span className="text-brand-cyan">Night Shift</span></div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Monitoring window · 10:00 PM – 6:00 AM</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ClockChip />
            <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-btn" className="text-white/70 hover:text-white"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
            <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn" className="text-white/70 hover:text-white"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Shift Details */}
        <ShiftDetails current={current} operators={operators} onAddOperator={addOperator} />

        {/* 1️⃣ Start of Shift */}
        <StartShiftSection current={current} clients={clients} operators={operators} onStart={startShift} />

        {/* 3️⃣ Live Summary — now above Camera Log */}
        <LiveSummary live={live} clients={clients} />

        {/* 2️⃣ Camera Log */}
        {current && (
          <CameraLogSection shift={current} clients={clients} events={events} onOffline={logOffline} onOnline={markOnline} />
        )}

        {/* 4️⃣ End of Shift */}
        {current && (
          <EndShiftSection current={current} clients={clients} events={events} onComplete={completeShift} />
        )}

        {/* Past shifts / open report */}
        <PastShifts pin={pin} onOpenReport={openReport} onShareReport={shareReport} />
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function ClockChip() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return <div className="text-xs font-mono text-white/70 border border-white/10 rounded-md px-2 py-1 bg-black/40" data-testid="pc-clock">{now.toLocaleTimeString("en-IN")}</div>;
}

/* Live elapsed timer since a given ISO timestamp — updates every second */
function LiveDuration({ since, className = "", testid }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const start = since ? new Date(since).getTime() : now;
  const elapsed = Math.max(0, Math.floor((now - start) / 1000));
  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const label = h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  // Escalate colour intensity as downtime grows
  const tone =
    elapsed < 60 ? "bg-amber-500/15 text-amber-200 border-amber-400/40" :
    elapsed < 300 ? "bg-orange-500/20 text-orange-200 border-orange-400/50" :
    "bg-rose-500/25 text-rose-100 border-rose-400/60 animate-pulse";
  return (
    <span
      data-testid={testid}
      title={`Offline for ${label}`}
      className={`inline-flex items-center gap-1 font-mono font-bold text-[11px] px-2 py-0.5 rounded-md border ${tone} ${className}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
      {label}
    </span>
  );
}

function SectionCard({ label, title, children, testid, right }) {
  return (
    <section data-testid={testid} className="rounded-2xl border border-white/10 bg-[#0F0F13] p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-cyan/80 font-bold">{label}</div>
          <h2 className="font-heading text-lg sm:text-xl font-extrabold text-white">{title}</h2>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

/* ------------------------------------------------------------------ */
function ShiftDetails({ current, operators, onAddOperator }) {
  return (
    <SectionCard label="Shift Details" title="Session" testid="section-shift-details">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
        <Info k="Date" v={new Date().toLocaleDateString("en-IN")} />
        <Info k="Operator" v={current?.operator || "—"} />
        <Info k="Shift Start" v={current?.shift_start ? new Date(current.shift_start).toLocaleTimeString("en-IN") : "—"} />
        <Info k="Status" v={current ? <span className="text-emerald-300 font-semibold">Active</span> : <span className="text-white/50">Not started</span>} />
      </div>
      <div className="text-[11px] text-white/40 mt-3">Operators available: {operators.length} · <button onClick={() => {
        const n = window.prompt("New night operator name:");
        if (n) onAddOperator(n.trim());
      }} className="text-brand-cyan hover:underline" data-testid="add-operator-inline">+ Add operator</button></div>
    </SectionCard>
  );
}

function Info({ k, v }) {
  return <div><div className="text-[10px] uppercase tracking-widest text-white/40">{k}</div><div className="text-white font-semibold">{v || "—"}</div></div>;
}

/* ------------------------------------------------------------------ */
function StartShiftSection({ current, clients, operators, onStart }) {
  const [operator, setOperator] = useState("");
  const [verified, setVerified] = useState(false);
  const [rechActive, setRechActive] = useState(0);
  const [rechExpired, setRechExpired] = useState(0);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setRows(clients.map((c) => ({
      client_id: c.id,
      total: c.total_cameras || 0,
      online: c.total_cameras || 0,
      offline: 0,
      dvr_time: "",
      action: "",
    })));
  }, [clients]);

  const setRow = (idx, k, v) => {
    setRows(rows.map((r, i) => i === idx ? { ...r, [k]: (typeof v === "string" && (k === "dvr_time" || k === "action") ? v : Number(v) || 0) } : r));
  };

  if (current) {
    return (
      <SectionCard label="1️⃣ Start of Shift" title="Started · Cameras baseline captured" testid="section-start">
        <div className="text-sm text-white/70">Shift already active. Skip to <b className="text-white">Camera Log</b> below.</div>
      </SectionCard>
    );
  }

  const submit = () => {
    if (!operator) return toast.error("Select an operator");
    const now = new Date();
    const nowSec = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    const actualStr = now.toTimeString().slice(0, 8);
    const stamped = rows.map((r) => {
      const out = { ...r, actual_time: actualStr };
      if (r.dvr_time) {
        const p = r.dvr_time.split(":").map((x) => parseInt(x, 10));
        if (p.length >= 2 && !p.some(isNaN)) {
          const [h, m, s = 0] = p;
          let diff = (h * 3600 + m * 60 + s) - nowSec;
          if (diff > 12 * 3600) diff -= 24 * 3600;
          if (diff < -12 * 3600) diff += 24 * 3600;
          out.diff_seconds = diff;
        }
      }
      return out;
    });
    onStart({
      operator, verified,
      recharge_active: rechActive, recharge_expired: rechExpired,
      cameras_start: stamped,
    });
  };

  return (
    <SectionCard label="1️⃣ Start of Shift" title="10 PM baseline + DVR/NVR verification" testid="section-start"
      right={<span className="text-[10px] uppercase tracking-widest text-white/40">Auto PC time captured on save</span>}>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <div className="sm:col-span-2">
          <Label className="text-white/70 text-xs">Operator *</Label>
          <Select value={operator} onValueChange={setOperator}>
            <SelectTrigger data-testid="start-operator-select" className="bg-black/40 border-white/10"><SelectValue placeholder="Select operator" /></SelectTrigger>
            <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
              {operators.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 pt-6 text-sm cursor-pointer">
          <input data-testid="verify-time-chk" type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} className="accent-brand-cyan h-4 w-4" />
          Date &amp; time verified
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-white/70 text-xs">🟢 Active</Label>
            <Input data-testid="recharge-active" type="number" value={rechActive} onChange={(e) => setRechActive(e.target.value)} className="bg-black/40 border-white/10" />
          </div>
          <div>
            <Label className="text-white/70 text-xs">🔴 Expired</Label>
            <Input data-testid="recharge-expired" type="number" value={rechExpired} onChange={(e) => setRechExpired(e.target.value)} className="bg-black/40 border-white/10" />
          </div>
        </div>
      </div>

      <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-2">
        <span className="text-brand-cyan">Date &amp; Time Verification</span>
        <span className="text-white/40">· Enter DVR/NVR time — diff auto-computed against PC time</span>
      </div>
      <CameraStatusTable rows={rows} clients={clients} onChange={setRow} showVerification />

      <div className="mt-4 flex justify-end">
        <Button data-testid="start-shift-btn" onClick={submit} className="bg-emerald-500/25 border border-emerald-500/60 text-emerald-100 hover:bg-emerald-500/35 font-bold">
          <PlayCircle className="h-4 w-4 mr-2" />Save &amp; Start Monitoring
        </Button>
      </div>
    </SectionCard>
  );
}

function CameraStatusTable({ rows, clients, onChange, showRemarks = false, showVerification = false }) {
  const clientMap = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);

  // Live actual-time refresh (every second) — only used for the verification variant
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    if (!showVerification) return;
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, [showVerification]);
  const actualHHMMSS = now.toTimeString().slice(0, 8);

  const diffLabel = (dvr) => {
    if (!dvr) return null;
    // Accept HH:MM or HH:MM:SS
    const parts = dvr.split(":").map((x) => parseInt(x, 10));
    if (parts.length < 2 || parts.some(isNaN)) return null;
    const [h, m, s = 0] = parts;
    const dvrSec = h * 3600 + m * 60 + s;
    const nowSec = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
    let diff = dvrSec - nowSec;
    // Handle midnight wrap: pick the closest signed
    if (diff > 12 * 3600) diff -= 24 * 3600;
    if (diff < -12 * 3600) diff += 24 * 3600;
    return diff;
  };
  const statusFor = (d) => {
    if (d === null || d === undefined) return { icon: "—", color: "text-white/40" };
    const abs = Math.abs(d);
    if (abs <= 60) return { icon: "OK", color: "text-emerald-400" };
    if (abs <= 300) return { icon: "WARN", color: "text-amber-300" };
    return { icon: "OFF", color: "text-rose-400" };
  };
  const fmtDiff = (d) => {
    if (d === null || d === undefined) return "—";
    const s = Math.abs(d);
    const sign = d < 0 ? "-" : "+";
    if (s < 60) return `${sign}${s} sec`;
    return `${sign}${Math.round(s / 60)} min`;
  };

  if (!rows.length) return <div className="text-white/40 text-sm text-center py-4">Add cameras to your clients (Admin → Billing → Clients) to see them here.</div>;
  return (
    <div className="rounded-lg border border-white/10 overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-white/5 text-white/60 text-[11px] uppercase tracking-widest">
          <tr>
            <th className="text-left px-3 py-2">Client</th>
            {showVerification && <th className="text-left px-2 py-2 hidden md:table-cell">Location</th>}
            {showVerification && <th className="text-center px-2 py-2 w-28">DVR/NVR Time</th>}
            {showVerification && <th className="text-center px-2 py-2 w-24">Actual</th>}
            {showVerification && <th className="text-center px-2 py-2 w-20">Diff</th>}
            {showVerification && <th className="text-center px-2 py-2 w-20">Status</th>}
            {showVerification && <th className="text-left px-2 py-2 w-32 hidden sm:table-cell">Action</th>}
            <th className="text-center px-3 py-2 w-16">Total</th>
            <th className="text-center px-3 py-2 w-20">Online</th>
            <th className="text-center px-3 py-2 w-20">Offline</th>
            {showRemarks && <th className="text-left px-3 py-2">Remarks</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const cli = clientMap[r.client_id] || {};
            const diff = showVerification ? diffLabel(r.dvr_time) : null;
            const st = statusFor(diff);
            return (
              <tr key={r.client_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`cam-row-${r.client_id}`}>
                <td className="px-3 py-2 text-white font-medium">{cli.name || "—"}</td>
                {showVerification && <td className="px-2 py-1 text-white/60 text-xs hidden md:table-cell">{cli.address || "—"}</td>}
                {showVerification && (
                  <td className="px-2 py-1">
                    <div className="flex gap-1 items-center">
                      <Input
                        data-testid={`dvr-time-${r.client_id}`}
                        type="time" step="1"
                        value={r.dvr_time || ""}
                        onChange={(e) => onChange(idx, "dvr_time", e.target.value)}
                        className="h-8 bg-black/40 border-white/10 text-center font-mono text-xs"
                      />
                      <button
                        type="button"
                        data-testid={`dvr-now-${r.client_id}`}
                        onClick={() => onChange(idx, "dvr_time", actualHHMMSS)}
                        title="Set to current PC time"
                        className="h-8 px-2 rounded-md border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan text-[10px] font-semibold hover:bg-brand-cyan/20"
                      >NOW</button>
                    </div>
                  </td>
                )}
                {showVerification && <td className="px-2 py-1 text-center text-white/70 font-mono text-xs">{actualHHMMSS}</td>}
                {showVerification && <td className={`px-2 py-1 text-center font-mono text-xs ${st.color}`}>{fmtDiff(diff)}</td>}
                {showVerification && (
                  <td className={`px-2 py-1 text-center font-bold text-[11px] ${st.color}`}>{st.icon}</td>
                )}
                {showVerification && (
                  <td className="px-2 py-1 hidden sm:table-cell">
                    <Input
                      data-testid={`dvr-action-${r.client_id}`}
                      value={r.action || ""}
                      onChange={(e) => onChange(idx, "action", e.target.value)}
                      placeholder={st.icon === "OK" ? "OK" : "Corrected"}
                      className="h-8 bg-black/40 border-white/10 text-xs"
                    />
                  </td>
                )}
                <td className="px-2 py-1"><Input value={r.total} onChange={(e) => onChange(idx, "total", e.target.value)} type="number" className="h-8 bg-black/40 border-white/10 text-center" /></td>
                <td className="px-2 py-1"><Input value={r.online} onChange={(e) => onChange(idx, "online", e.target.value)} type="number" className="h-8 bg-black/40 border-white/10 text-center text-emerald-300" /></td>
                <td className="px-2 py-1"><Input value={r.offline} onChange={(e) => onChange(idx, "offline", e.target.value)} type="number" className="h-8 bg-black/40 border-white/10 text-center text-rose-300" /></td>
                {showRemarks && (
                  <td className="px-2 py-1"><Input value={r.remarks || ""} onChange={(e) => onChange(idx, "remarks", e.target.value)} placeholder="Notes…" className="h-8 bg-black/40 border-white/10" /></td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function CameraLogSection({ shift, clients, events, onOffline, onOnline }) {
  const [clientId, setClientId] = useState("");
  const [camera, setCamera] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [reason, setReason] = useState("Internet");
  const [remarks, setRemarks] = useState("");

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
  const openEvents = events.filter((e) => !e.online_time);
  const cams = (clientMap[clientId]?.camera_names) || [];

  // Reset camera choice when client changes
  useEffect(() => { setCamera(""); setCustomMode(false); }, [clientId]);

  const submitOffline = (e) => {
    e.preventDefault();
    if (!clientId) return toast.error("Select a client");
    const label = (camera || "").trim();
    if (!label) return toast.error("Select or enter a camera");
    const finalClientId = clientId === "__all_clients__" ? "ALL" : clientId;
    onOffline({ shift_id: shift.id, client_id: finalClientId, camera_label: label, reason, remarks: remarks || null });
    setCamera(""); setRemarks(""); setCustomMode(false);
  };

  return (
    <SectionCard label="2️⃣ Camera Log" title="Offline / Online events" testid="section-camera-log">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <form onSubmit={submitOffline} className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 space-y-2 text-sm">
          <div className="flex items-center gap-2 text-rose-300 font-bold"><VideoOff className="h-4 w-4" />Log camera OFFLINE</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white/60 text-[10px] uppercase tracking-widest">Client</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="off-client" className="bg-black/40 border-white/10 h-8 text-xs"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                  <SelectItem value="__all_clients__">🌐 All clients</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/60 text-[10px] uppercase tracking-widest">Camera</Label>
              {customMode || (clientId && clientId !== "__all_clients__" && cams.length === 0) ? (
                <div className="flex gap-1">
                  <Input data-testid="off-camera" value={camera} onChange={(e) => setCamera(e.target.value)} placeholder="e.g. CAM 07" className="bg-black/40 border-white/10 h-8 text-xs" />
                  {cams.length > 0 && (
                    <button type="button" onClick={() => { setCustomMode(false); setCamera(""); }} data-testid="off-camera-back" className="px-2 rounded-md border border-white/15 text-white/60 hover:bg-white/5 text-[10px]">↺</button>
                  )}
                </div>
              ) : (
                <Select value={camera} onValueChange={(v) => { if (v === "__custom__") { setCustomMode(true); setCamera(""); } else { setCamera(v); } }} disabled={!clientId}>
                  <SelectTrigger data-testid="off-camera-select" className="bg-black/40 border-white/10 h-8 text-xs"><SelectValue placeholder={clientId ? "Select camera" : "Pick client first"} /></SelectTrigger>
                  <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                    <SelectItem value="All cameras">All cameras</SelectItem>
                    {cams.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    <SelectItem value="__custom__">➕ Custom…</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {clientId && clientId !== "__all_clients__" && cams.length === 0 && (
                <div className="text-[9px] text-amber-300/80 mt-0.5">No saved cameras — enter manually.</div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-white/60 text-[10px] uppercase tracking-widest">Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="off-reason" className="bg-black/40 border-white/10 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                  {REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/60 text-[10px] uppercase tracking-widest">Remarks</Label>
              <Input data-testid="off-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional…" className="bg-black/40 border-white/10 h-8 text-xs" />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-white/40">Offline time = PC time on save</span>
            <Button size="sm" type="submit" data-testid="off-save-btn" className="bg-rose-500/30 border border-rose-500/60 text-white hover:bg-rose-500/40 font-bold h-8"><Plus className="h-3.5 w-3.5 mr-1" />Save</Button>
          </div>
        </form>

        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2">
          <div className="flex items-center gap-2 text-emerald-300 font-bold text-sm"><Video className="h-4 w-4" />Open events · mark ONLINE</div>
          {openEvents.length === 0 ? (
            <div className="text-white/50 text-xs py-4 text-center">No offline events pending. 🎉</div>
          ) : (
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {openEvents.map((e) => (
                <div key={e.id} data-testid={`open-event-${e.id}`} className="rounded-lg bg-black/40 border border-white/10 px-2.5 py-1.5 flex items-center justify-between gap-2">
                  <div className="min-w-0 text-xs">
                    <div className="font-semibold text-white truncate">{(e.client_id === "ALL" ? "All clients" : clientMap[e.client_id]?.name) || "—"} · <span className="text-rose-300">{e.camera_label}</span></div>
                    <div className="text-[10px] text-white/50">Off at {new Date(e.offline_time).toLocaleTimeString("en-IN")} · {e.reason}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <LiveDuration since={e.offline_time} testid={`open-timer-${e.id}`} />
                    <Button size="sm" data-testid={`online-btn-${e.id}`} onClick={() => onOnline(e.id)} className="bg-emerald-500/25 border border-emerald-500/60 text-emerald-100 hover:bg-emerald-500/35 font-bold h-7 text-xs">
                      <Video className="h-3.5 w-3.5 mr-1" />Online
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-widest text-white/50 mb-2">Recent events</div>
        {events.length === 0 ? (
          <div className="text-white/40 text-sm text-center py-4">No events yet.</div>
        ) : (
          <div className="rounded-lg border border-white/10 divide-y divide-white/5 max-h-56 overflow-y-auto">
            {events.map((e) => (
              <div key={e.id} className="px-3 py-2 flex items-center justify-between text-sm">
                <div className="min-w-0">
                  <div className="text-white">
                    {(e.client_id === "ALL" ? "All clients" : clientMap[e.client_id]?.name) || "—"} · <span className="text-white/70">{e.camera_label}</span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] uppercase ${e.online_time ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>{e.online_time ? "Resolved" : "Open"}</span>
                  </div>
                  <div className="text-[10px] text-white/40">Off {new Date(e.offline_time).toLocaleTimeString("en-IN")}{e.online_time ? ` · On ${new Date(e.online_time).toLocaleTimeString("en-IN")}` : ""} · {e.reason}</div>
                </div>
                {e.online_time
                  ? <div className="text-emerald-300 text-xs font-mono" data-testid={`event-downtime-${e.id}`}>{Math.round((e.downtime_minutes || 0))} min</div>
                  : <LiveDuration since={e.offline_time} testid={`event-timer-${e.id}`} />}
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

/* ------------------------------------------------------------------ */
function LiveSummary({ live, clients }) {
  const totalCams = clients.reduce((a, c) => a + (c.total_cameras || 0), 0);
  const totalClients = clients.length;
  return (
    <SectionCard label="3️⃣ Live Summary" title="At a glance" testid="section-live"
      right={<span className="text-[10px] uppercase tracking-widest text-white/40">Auto-refreshes every 30s</span>}>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat k="Clients Active" v={live?.clients_active ?? 0} accent="emerald" testid="stat-clients-active" />
        <Stat k="Clients Offline" v={live?.clients_offline ?? 0} accent="rose" testid="stat-clients-offline" />
        <Stat k="Cameras Online" v={live?.cameras_online ?? totalCams} accent="emerald" testid="stat-cams-online" />
        <Stat k="Cameras Offline" v={live?.cameras_offline ?? 0} accent="rose" testid="stat-cams-offline" />
        <Stat k="Open Issues" v={live?.open_events ?? 0} accent="amber" testid="stat-open-issues" />
      </div>
      <div className="text-[11px] text-white/40 mt-3">Universe: {totalClients} clients · {totalCams} cameras total</div>
    </SectionCard>
  );
}

function Stat({ k, v, accent, testid }) {
  const cx = accent === "rose" ? "text-rose-400" : accent === "emerald" ? "text-emerald-400" : accent === "amber" ? "text-amber-300" : "text-white";
  return (
    <div data-testid={testid} className="rounded-xl border border-white/10 bg-black/30 p-3">
      <div className="text-[10px] uppercase tracking-widest text-white/50">{k}</div>
      <div className={`font-heading text-2xl font-extrabold mt-1 ${cx}`}>{v}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function EndShiftSection({ current, clients, events, onComplete }) {
  const [rows, setRows] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [ck, setCk] = useState({ all: false, off: false, pend: false, notif: false });
  const [sig1, setSig1] = useState("");
  const [sig2, setSig2] = useState("");

  useEffect(() => {
    // Seed sig1 with the shift's registered operator
    setSig1(current?.operator || "");
    setSig2("");
    // Seed final camera status from start snapshot
    const startRows = current?.start?.cameras_start || [];
    if (startRows.length) {
      setRows(startRows.map((r) => ({ client_id: r.client_id, online: r.online, offline: r.offline, remarks: "" })));
    } else {
      setRows(clients.map((c) => ({ client_id: c.id, online: c.total_cameras || 0, offline: 0, remarks: "" })));
    }
    // eslint-disable-next-line
  }, [current?.id, clients.length]);

  const setRow = (idx, k, v) => {
    setRows(rows.map((r, i) => i === idx ? { ...r, [k]: (k === "remarks" ? v : Number(v) || 0) } : r));
  };
  const pending = events.filter((e) => !e.online_time).length;

  const submit = () => {
    if (!sig1.trim()) return toast.error("Operator 1 signature required");
    if (pending > 0 && !window.confirm(`${pending} event(s) still open. Complete shift anyway?`)) return;
    onComplete({
      cameras_end: rows,
      final_remarks: remarks || null,
      check_all_clients: ck.all,
      check_offline_reviewed: ck.off,
      check_pending_noted: ck.pend,
      check_client_notified: ck.notif,
      sign_operator_1: sig1.trim(),
      sign_operator_2: sig2.trim() || null,
    });
  };

  return (
    <SectionCard label="4️⃣ End of Shift" title="6 AM handover" testid="section-end"
      right={<span className="text-[10px] uppercase tracking-widest text-white/40">Auto logout time on complete</span>}>
      <CameraStatusTable rows={rows} clients={clients} onChange={setRow} showRemarks />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <Label className="text-white/70 text-xs">Final remarks</Label>
          <Textarea data-testid="end-remarks" rows={4} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any notes for the day handover…" className="bg-black/40 border-white/10" />
        </div>
        <div className="grid grid-cols-1 gap-2 text-sm">
          <label className="flex items-center gap-2 cursor-pointer"><input data-testid="ck-all" type="checkbox" checked={ck.all} onChange={(e) => setCk({ ...ck, all: e.target.checked })} className="accent-brand-cyan h-4 w-4" />All clients checked</label>
          <label className="flex items-center gap-2 cursor-pointer"><input data-testid="ck-off" type="checkbox" checked={ck.off} onChange={(e) => setCk({ ...ck, off: e.target.checked })} className="accent-brand-cyan h-4 w-4" />Offline cameras reviewed</label>
          <label className="flex items-center gap-2 cursor-pointer"><input data-testid="ck-pend" type="checkbox" checked={ck.pend} onChange={(e) => setCk({ ...ck, pend: e.target.checked })} className="accent-brand-cyan h-4 w-4" />Pending issues noted</label>
          <label className="flex items-center gap-2 cursor-pointer"><input data-testid="ck-notif" type="checkbox" checked={ck.notif} onChange={(e) => setCk({ ...ck, notif: e.target.checked })} className="accent-brand-cyan h-4 w-4" />Client notified if required</label>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-brand-cyan/25 bg-brand-cyan/5 p-4">
        <div className="text-[11px] uppercase tracking-widest text-brand-cyan mb-2 font-bold">✍️ Handover Signatures</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-white/70 text-xs">Operator 1 signature *</Label>
            <Input data-testid="sig-operator-1" value={sig1} onChange={(e) => setSig1(e.target.value)} placeholder="Type full name to sign" className="bg-black/40 border-white/10 font-signature" />
          </div>
          <div>
            <Label className="text-white/70 text-xs">Operator 2 signature (optional)</Label>
            <Input data-testid="sig-operator-2" value={sig2} onChange={(e) => setSig2(e.target.value)} placeholder="Second operator name" className="bg-black/40 border-white/10 font-signature" />
          </div>
        </div>
        <div className="text-[10px] text-white/40 mt-2">Both operators can sign before submitting — recorded in the daily report as proof of joint handover.</div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[11px] text-white/50">
          {pending > 0 ? <span className="text-amber-300 inline-flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" />{pending} event(s) still open</span>
            : <span className="text-emerald-300 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />All events resolved</span>}
        </div>
        <Button data-testid="complete-shift-btn" onClick={submit} className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold">
          <ListChecks className="h-4 w-4 mr-2" />Complete Shift &amp; Generate Report
        </Button>
      </div>
    </SectionCard>
  );
}

/* ------------------------------------------------------------------ */
function PastShifts({ pin, onOpenReport, onShareReport }) {
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const r = await axios.get(`${API}/monitoring/shifts`, { headers: { "X-Operator-Pin": pin } });
      setItems((r.data || []).filter((s) => s.status === "completed").slice(0, 8));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  if (!items.length) return null;
  return (
    <SectionCard label="History" title="Recent completed shifts" testid="section-history">
      <div className="rounded-lg border border-white/10 divide-y divide-white/5">
        {items.map((s) => (
          <div key={s.id} className="px-3 py-2 flex items-center justify-between gap-2 text-sm" data-testid={`past-${s.id}`}>
            <div>
              <div className="text-white font-semibold">{s.date} · <span className="text-white/70">{s.operator}</span></div>
              <div className="text-[10px] text-white/40">Start {s.shift_start ? new Date(s.shift_start).toLocaleTimeString("en-IN") : "—"} · End {s.shift_end ? new Date(s.shift_end).toLocaleTimeString("en-IN") : "—"}</div>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" onClick={() => onOpenReport(s.id, "view")} data-testid={`past-view-${s.id}`} title="View" className="border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/10">
                <Eye className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => onOpenReport(s.id, "print")} data-testid={`past-print-${s.id}`} title="Print / PDF" className="border-white/20 text-white hover:bg-white/10">
                <Printer className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => onShareReport(s.id)} data-testid={`past-share-${s.id}`} title="Share on WhatsApp" className="border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10">
                <Share2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

/* ------------------------------------------------------------------ */
function esc(s) { return String(s ?? "").replace(/[&<>"']/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c])); }

function renderReportBody(data) {
  const s = data.shift || {};
  const t = data.totals || {};
  const cm = data.client_map || {};
  const evs = data.events || [];
  const startRows = (s.start?.cameras_start || []).map((r) => {
    const st = r.diff_seconds == null ? { icon: "—", cls: "" } :
      Math.abs(r.diff_seconds) <= 60 ? { icon: "OK", cls: "color:#059669" } :
      Math.abs(r.diff_seconds) <= 300 ? { icon: "WARN", cls: "color:#d97706" } :
      { icon: "OFF", cls: "color:#dc2626" };
    const diffLabel = r.diff_seconds == null ? "—" :
      Math.abs(r.diff_seconds) < 60 ? `${r.diff_seconds >= 0 ? "+" : "-"}${Math.abs(r.diff_seconds)} sec` :
      `${r.diff_seconds >= 0 ? "+" : "-"}${Math.round(Math.abs(r.diff_seconds) / 60)} min`;
    return `<tr>
      <td>${esc(cm[r.client_id]?.name || "—")}</td>
      <td class="c">${esc(r.dvr_time || "—")}</td>
      <td class="c">${esc(r.actual_time || "—")}</td>
      <td class="c">${diffLabel}</td>
      <td class="c" style="${st.cls};font-weight:700">${st.icon}</td>
      <td>${esc(r.action || "")}</td>
      <td class="c">${r.total || 0}</td>
      <td class="c">${r.online || 0}</td>
      <td class="c">${r.offline || 0}</td>
    </tr>`;
  }).join("");
  const endRows = (s.end?.cameras_end || []).map((r) =>
    `<tr><td>${esc(cm[r.client_id]?.name || "—")}</td><td class="c">${r.online || 0}</td><td class="c">${r.offline || 0}</td><td>${esc(r.remarks || "")}</td></tr>`
  ).join("");
  const evRows = evs.map((e) =>
    `<tr><td>${esc(e.client_id === "ALL" ? "All clients" : (cm[e.client_id]?.name || "—"))}</td><td>${esc(e.camera_label)}</td><td>${esc(e.reason)}</td><td class="c">${new Date(e.offline_time).toLocaleTimeString("en-IN")}</td><td class="c">${e.online_time ? new Date(e.online_time).toLocaleTimeString("en-IN") : "—"}</td><td class="r">${e.downtime_minutes != null ? Math.round(e.downtime_minutes)+" min" : "—"}</td><td>${esc(e.remarks || "")}</td></tr>`
  ).join("");
  const chk = s.end || {};
  return `<h1>SECURE WATCH · Night Monitoring Report</h1>
  <div class="sub">Date <b>${esc(s.date)}</b> · Operator <b>${esc(s.operator)}</b> · Start ${s.shift_start ? new Date(s.shift_start).toLocaleString("en-IN") : "—"} · End ${s.shift_end ? new Date(s.shift_end).toLocaleString("en-IN") : "—"}</div>
  <div class="cards">
    <div class="card"><div class="l">Total Clients</div><div class="v">${t.total_clients || 0}</div></div>
    <div class="card"><div class="l">Cameras Total</div><div class="v">${t.cameras_total || 0}</div></div>
    <div class="card"><div class="l">Events</div><div class="v">${t.events_total || 0}</div></div>
    <div class="card"><div class="l">Resolved</div><div class="v">${t.events_resolved || 0}</div></div>
    <div class="card"><div class="l">Downtime</div><div class="v">${Math.round(t.total_downtime_minutes || 0)} min</div></div>
  </div>
  <div class="sub">Recharge · Active <b>${s.start?.recharge_active || 0}</b> · Expired <b>${s.start?.recharge_expired || 0}</b> · Time verified: ${s.start?.verified ? "Yes" : "No"}</div>
  <h2>Date &amp; Time Verification · Camera status at start</h2>
  <table><thead><tr><th>Client</th><th class="c">DVR/NVR</th><th class="c">Actual</th><th class="c">Diff</th><th class="c">Status</th><th>Action</th><th class="c">Total</th><th class="c">Online</th><th class="c">Offline</th></tr></thead><tbody>${startRows || '<tr><td colspan="9" style="text-align:center;color:#999">—</td></tr>'}</tbody></table>
  <h2>Camera events</h2>
  <table><thead><tr><th>Client</th><th>Camera</th><th>Reason</th><th class="c">Offline</th><th class="c">Online</th><th class="r">Downtime</th><th>Remarks</th></tr></thead><tbody>${evRows || '<tr><td colspan="7" style="text-align:center;color:#999">No events</td></tr>'}</tbody></table>
  <h2>Camera status at end</h2>
  <table><thead><tr><th>Client</th><th class="c">Online</th><th class="c">Offline</th><th>Remarks</th></tr></thead><tbody>${endRows || '<tr><td colspan="4" style="text-align:center;color:#999">—</td></tr>'}</tbody></table>
  <h2>Handover checks</h2>
  <div class="chks">${chk.check_all_clients ? "☑" : "☐"} All clients checked</div>
  <div class="chks">${chk.check_offline_reviewed ? "☑" : "☐"} Offline cameras reviewed</div>
  <div class="chks">${chk.check_pending_noted ? "☑" : "☐"} Pending issues noted</div>
  <div class="chks">${chk.check_client_notified ? "☑" : "☐"} Client notified if required</div>
  <h2>Operator remarks</h2>
  <div style="font-size:11px;white-space:pre-wrap;border:1px solid #eee;padding:8px;border-radius:6px;background:#fafafa">${esc(chk.final_remarks || "—")}</div>
  <h2>Signatures</h2>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:8px">
    <div style="border:1px solid #eee;border-radius:8px;padding:10px">
      <div style="font-size:9px;letter-spacing:1.5px;color:#888;text-transform:uppercase">Operator 1</div>
      <div style="font-size:16px;font-style:italic;color:#111;padding:8px 0;border-bottom:1px solid #ddd;min-height:32px">${esc(chk.sign_operator_1 || "—")}</div>
    </div>
    <div style="border:1px solid #eee;border-radius:8px;padding:10px">
      <div style="font-size:9px;letter-spacing:1.5px;color:#888;text-transform:uppercase">Operator 2</div>
      <div style="font-size:16px;font-style:italic;color:#111;padding:8px 0;border-bottom:1px solid #ddd;min-height:32px">${esc(chk.sign_operator_2 || "—")}</div>
    </div>
  </div>
  <p class="foot">Generated ${new Date().toLocaleString("en-IN")} · SECURE WATCH Night Monitoring</p>`;
}

const REPORT_CSS = `
*{box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif}
body{margin:28px;color:#111;background:#fff}
h1{font-size:22px;margin:0 0 4px}
.sub{color:#666;font-size:12px;margin-bottom:14px}
.cards{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin:8px 0 16px}
.card{border:1px solid #eee;border-radius:8px;padding:10px}
.card .l{font-size:9px;letter-spacing:1.5px;color:#888;text-transform:uppercase}
.card .v{font-size:16px;font-weight:800;margin-top:2px}
h2{font-size:11px;text-transform:uppercase;letter-spacing:1.5px;color:#555;margin:14px 0 4px;border-bottom:1px solid #eee;padding-bottom:4px}
table{border-collapse:collapse;width:100%;margin-bottom:14px}
th,td{border:1px solid #eee;padding:6px 8px;font-size:11px}
th{background:#f6f6f6;text-align:left;text-transform:uppercase;font-size:9px;letter-spacing:1.5px;color:#666}
td.r{text-align:right}td.c{text-align:center}
.foot{margin-top:16px;font-size:10px;color:#888}
.chks{font-size:11px;margin:2px 0}
@media print{@page{size:A4;margin:12mm}}
`;

function renderReportHtml(data, forCanvas = false) {
  const s = data.shift || {};
  return `<!doctype html><html><head><meta charset="utf-8"/><title>Night Monitoring · ${esc(s.date)} · ${esc(s.operator)}</title>
  <style>${REPORT_CSS}</style></head><body>
  ${renderReportBody(data)}
  ${forCanvas ? "" : `<script>window.onload=()=>{setTimeout(()=>window.print(),300);}</script>`}
  </body></html>`;
}
